/**
 * Referral commission ledger — 10% of paid song requests, tips excluded.
 * Separate from the artist's 70% payout rail (Payouts page / artist_payout_requests).
 *
 * On a paid song of £100 (no tip):
 *   Referred artist 70%  ·  YSR 20%  ·  Referrer 10%
 * Tips stay with the performing artist and never enter this ledger.
 */

import { isAdminAnalyticsPaidRequest, ledgerCurrencyForRequest } from '@/lib/payoutLedger';

export const REFERRER_SHARE_RATE = 0.10;
export const PLATFORM_NET_RATE = 0.20;
export const REFERRED_ARTIST_SHARE_RATE = 0.70;

export function money(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export function roundMoney(value) {
  return Number(money(value).toFixed(2));
}

export function requestLedgerCurrency(req) {
  return ledgerCurrencyForRequest(req);
}

/**
 * Song amount that referral commission is calculated on.
 * New A2B/PayPal rows: amount_paid = song, tip separate, total_paid = song + tip.
 * Legacy rows: amount_paid often included the tip, so subtract it.
 */
export function splitPaidRequestAmounts(req) {
  const tip = Math.max(0, money(req?.tip));
  const isTipOnly = req?.is_tip_only === true;
  if (isTipOnly) {
    const total = tip > 0 ? tip : money(req?.amount_paid || req?.total_paid);
    return { songGross: 0, tip: total, total, eligible: false };
  }

  const totalPaid = money(req?.total_paid);
  const amountPaid = money(req?.amount_paid);
  const total = totalPaid > 0 ? totalPaid : amountPaid;
  const songGross = totalPaid > 0
    ? Math.max(0, totalPaid - tip)
    : Math.max(0, amountPaid - tip);

  return { songGross, tip, total, eligible: songGross > 0 };
}

/** Paid customer song requests that generate referrer commission. Tips, demos, trials excluded. */
export function isReferralEligiblePaidRequest(req) {
  if (!isAdminAnalyticsPaidRequest(req)) return false;
  if (req?.is_tip_only === true) return false;
  if (req?.karaoke_host_id && !req?.artist_id) return false;
  return splitPaidRequestAmounts(req).eligible;
}

export function referralWorkingsForRequest(req) {
  const split = splitPaidRequestAmounts(req);
  const songGross = roundMoney(split.songGross);
  return {
    songGross,
    tipExcluded: roundMoney(split.tip),
    totalPaid: roundMoney(split.total),
    referredArtist70: roundMoney(songGross * REFERRED_ARTIST_SHARE_RATE),
    platform20: roundMoney(songGross * PLATFORM_NET_RATE),
    referrer10: roundMoney(songGross * REFERRER_SHARE_RATE),
    currency: requestLedgerCurrency(req),
    eligible: split.eligible,
  };
}

export function earningMatchesRequest(earning, requestId) {
  if (!earning || !requestId) return false;
  return String(earning.transactionId || '') === String(requestId);
}

export function sumByCurrency(rows, amountKey = 'amount') {
  const totals = {};
  (rows || []).forEach((row) => {
    const ccy = String(row.currency || 'GBP').toUpperCase();
    totals[ccy] = roundMoney((totals[ccy] || 0) + money(row[amountKey]));
  });
  return totals;
}

/**
 * The `payouts` collection also holds leftover Stripe Connect logs
 * (`payout_id`, `stripe_account_id`, amounts in pence). Those are not
 * referral ledger requests from Refer → Payouts.
 */
export function isReferralPayoutDoc(payout) {
  if (!payout || typeof payout !== 'object') return false;
  if (payout.ledger === 'referral') return true;
  if (payout.payout_id || payout.stripe_account_id) return false;
  if (payout.type === 'instant' || payout.type === 'standard') return false;
  return Boolean(payout.userId && (payout.requestedAt || payout.paymentMethod || payout.earningsBreakdown));
}
